import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import ImageUploadPreview from './ImageUploadPreview';

/**
 * WorkflowFieldRenderer - Renders custom workflow fields dynamically
 * Supports all field types including the new ones:
 * - daterange, multicheckbox, buttonselect, imageselect
 */
const WorkflowFieldRenderer = ({ workflow, formData, handleChange, errors }) => {
  if (!workflow || !workflow.steps || workflow.steps.length === 0) {
    return null;
  }

  const renderField = (field, fieldIndex) => {
    const fieldKey = field.name || `field_${fieldIndex}`;
    const fieldValue = formData[fieldKey] || '';
    const fieldError = errors[fieldKey];

    switch (field.type) {
      case 'text':
      case 'number':
        return (
          <div key={fieldIndex} className="space-y-2">
            <Label htmlFor={fieldKey}>
              {field.label || field.name}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={fieldKey}
              type={field.type}
              value={fieldValue}
              onChange={(e) => handleChange(fieldKey, e.target.value)}
              placeholder={field.placeholder}
              className={fieldError ? 'border-red-500' : ''}
            />
            {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
            {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
          </div>
        );

      case 'textarea':
        return (
          <div key={fieldIndex} className="space-y-2">
            <Label htmlFor={fieldKey}>
              {field.label || field.name}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              id={fieldKey}
              value={fieldValue}
              onChange={(e) => handleChange(fieldKey, e.target.value)}
              placeholder={field.placeholder}
              rows={4}
              className={fieldError ? 'border-red-500' : ''}
            />
            {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
            {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
          </div>
        );

      case 'date':
        return (
          <div key={fieldIndex} className="space-y-2">
            <Label htmlFor={fieldKey}>
              {field.label || field.name}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={fieldKey}
              type="date"
              value={fieldValue}
              onChange={(e) => handleChange(fieldKey, e.target.value)}
              className={fieldError ? 'border-red-500' : ''}
            />
            {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
            {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
          </div>
        );

      case 'daterange':
        return (
          <div key={fieldIndex} className="space-y-2">
            <Label>
              {field.label || field.name}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-gray-600">Start Date</Label>
                <Input
                  type="date"
                  value={formData[`${fieldKey}_start`] || ''}
                  onChange={(e) => handleChange(`${fieldKey}_start`, e.target.value)}
                  className={fieldError ? 'border-red-500' : ''}
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600">End Date</Label>
                <Input
                  type="date"
                  value={formData[`${fieldKey}_end`] || ''}
                  onChange={(e) => handleChange(`${fieldKey}_end`, e.target.value)}
                  className={fieldError ? 'border-red-500' : ''}
                />
              </div>
            </div>
            {field.maxDays && (
              <p className="text-xs text-gray-500">Maximum {field.maxDays} days between dates</p>
            )}
            {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
            {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
          </div>
        );

      case 'select':
        const options = field.options ? field.options.split(',').map(o => o.trim()) : [];
        return (
          <div key={fieldIndex} className="space-y-2">
            <Label htmlFor={fieldKey}>
              {field.label || field.name}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Select
              value={fieldValue}
              onValueChange={(value) => handleChange(fieldKey, value)}
            >
              <SelectTrigger className={fieldError ? 'border-red-500' : ''}>
                <SelectValue placeholder={field.placeholder || 'Select an option'} />
              </SelectTrigger>
              <SelectContent>
                {options.map((option, idx) => (
                  <SelectItem key={option.id || option.name || `${option.title || "option"}-${idx}`} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
            {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
          </div>
        );

      case 'radio':
        const radioOptions = field.options ? field.options.split(',').map(o => o.trim()) : [];
        return (
          <div key={fieldIndex} className="space-y-2">
            <Label>
              {field.label || field.name}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="space-y-2">
              {radioOptions.map((option, idx) => (
                <div key={option.id || option.name || `${option.title || "option"}-${idx}`} className="flex items-center gap-2">
                  <input
                    type="radio"
                    id={`${fieldKey}_${idx}`}
                    name={fieldKey}
                    value={option}
                    checked={fieldValue === option}
                    onChange={(e) => handleChange(fieldKey, e.target.value)}
                    className="h-4 w-4"
                  />
                  <Label htmlFor={`${fieldKey}_${idx}`} className="text-sm cursor-pointer">
                    {option}
                  </Label>
                </div>
              ))}
            </div>
            {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
            {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
          </div>
        );

      case 'checkbox':
        return (
          <div key={fieldIndex} className="flex items-center space-x-2">
            <Checkbox
              checked={!!fieldValue}
              onCheckedChange={(checked) => handleChange(fieldKey, checked)}
            />
            <Label className="text-sm cursor-pointer">
              {field.label || field.name}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            {field.helpText && <p className="text-xs text-gray-500 ml-6">{field.helpText}</p>}
          </div>
        );

      case 'multicheckbox':
        const multiOptions = field.options ? field.options.split(',').map(o => o.trim()) : [];
        const selectedValues = Array.isArray(fieldValue) ? fieldValue : [];
        
        return (
          <div key={fieldIndex} className="space-y-2">
            <Label>
              {field.label || field.name}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="space-y-2 border rounded-lg p-3 bg-gray-50">
              <p className="text-xs font-medium text-gray-700 mb-2">Select all that apply:</p>
              {multiOptions.map((option, idx) => (
                <div key={option.id || option.name || `${option.title || "option"}-${idx}`} className="flex items-center gap-2">
                  <Checkbox
                    checked={selectedValues.includes(option)}
                    onCheckedChange={(checked) => {
                      const newValues = checked
                        ? [...selectedValues, option]
                        : selectedValues.filter(v => v !== option);
                      handleChange(fieldKey, newValues);
                    }}
                  />
                  <Label className="text-sm cursor-pointer">{option}</Label>
                </div>
              ))}
            </div>
            {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
            {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
          </div>
        );

      case 'buttonselect':
        const buttonOptions = field.options ? field.options.split(',').map(o => o.trim()) : [];
        const selectedButtons = Array.isArray(fieldValue) ? fieldValue : [];
        
        return (
          <div key={fieldIndex} className="space-y-2">
            <Label>
              {field.label || field.name}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="flex flex-wrap gap-2">
              {buttonOptions.map((option, idx) => (
                <Button
                  key={option.id || option.name || `${option.title || "option"}-${idx}`}
                  type="button"
                  variant={selectedButtons.includes(option) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    const newValues = selectedButtons.includes(option)
                      ? selectedButtons.filter(v => v !== option)
                      : [...selectedButtons, option];
                    handleChange(fieldKey, newValues);
                  }}
                >
                  {option}
                </Button>
              ))}
            </div>
            {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
            {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
          </div>
        );

      case 'imageselect':
        const imageUrls = field.options ? field.options.split(',').map(o => o.trim()) : [];
        
        return (
          <div key={fieldIndex} className="space-y-2">
            <Label>
              {field.label || field.name}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="grid grid-cols-3 gap-3">
              {imageUrls.map((url, idx) => (
                <div
                  key={url.id || url.name || `${url.title || "url"}-${idx}`}
                  onClick={() => handleChange(fieldKey, url)}
                  className={`border-2 rounded-lg p-2 cursor-pointer transition-all ${
                    fieldValue === url ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="aspect-square bg-gray-200 rounded-md overflow-hidden">
                    <img src={url} alt={`Option ${idx + 1}`} className="w-full h-full object-cover" />
                  </div>
                  <p className="text-xs text-center mt-1">Option {idx + 1}</p>
                </div>
              ))}
            </div>
            {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
            {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
          </div>
        );

      case 'file':
      case 'gallery':
        return (
          <div key={fieldIndex} className="space-y-2">
            <Label>
              {field.label || field.name}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <ImageUploadPreview
              onFilesChange={(files) => handleChange(fieldKey, files)}
              existingFiles={Array.isArray(fieldValue) ? fieldValue : []}
            />
            {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
            {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
          </div>
        );

      default:
        return (
          <div key={fieldIndex} className="space-y-2">
            <Label>{field.label || field.name}</Label>
            <p className="text-sm text-gray-500">Unsupported field type: {field.type}</p>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {workflow.steps.map((step, stepIndex) => (
        <Card key={stepIndex} className="border-l-4 border-l-purple-500">
          <CardHeader className="pb-3 bg-purple-50">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-600 text-white font-bold text-sm">
                {step.step_number || stepIndex + 1}
              </div>
              <div>
                <CardTitle className="text-lg">{step.title}</CardTitle>
                {step.description && (
                  <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            {step.fields && step.fields.map((field, fieldIndex) => 
              renderField(field, fieldIndex)
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default WorkflowFieldRenderer;
